import { useState } from 'react'
import type { BusinessFlowStep, ConceptEntity, StepType } from '@/types/knowledge_graph'
import { PencilIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Card } from '@/components/ui/card'
import { InferenceTypeBadge } from './InferenceTypeBadge'

function stepTypeLabel (type: StepType): string {
  const map: Record<StepType, string> = {
    normal: '通常',
    exception: '例外',
    loop: 'ループ',
    parallel: '並行'
  }
  return map[type]
}

export function FlowStepRow ({ step, index, isException = false, entities = [], onUpdate }: {
  step: BusinessFlowStep
  index: number
  isException?: boolean
  entities?: ConceptEntity[]
  onUpdate: (changes: Partial<BusinessFlowStep>) => void
}): React.ReactElement {
  const [editing, setEditing] = useState(false)
  const [draftActor, setDraftActor] = useState('')
  const [draftEvent, setDraftEvent] = useState('')
  const [draftDescription, setDraftDescription] = useState('')
  const [draftStepType, setDraftStepType] = useState<StepType>('normal')

  function startEdit (): void {
    setDraftActor(step.actor)
    setDraftEvent(step.event)
    setDraftDescription(step.description ?? '')
    setDraftStepType(step.step_type)
    setEditing(true)
  }

  function commitEdit (): void {
    const trimmedActor = draftActor.trim()
    const trimmedEvent = draftEvent.trim()
    if (trimmedActor === '' || trimmedEvent === '') return
    const changes: Partial<BusinessFlowStep> = {}
    if (trimmedActor !== step.actor) changes.actor = trimmedActor
    if (trimmedEvent !== step.event) changes.event = trimmedEvent
    const trimmedDesc = draftDescription.trim()
    if (trimmedDesc !== (step.description ?? '')) changes.description = trimmedDesc
    if (draftStepType !== step.step_type) changes.step_type = draftStepType
    if (Object.keys(changes).length > 0) onUpdate(changes)
    setEditing(false)
  }

  function cancelEdit (): void { setEditing(false) }

  if (editing) {
    return (
      <Card className='flex flex-col items-stretch gap-1.5 p-2.5 rounded-lg shadow-none'>
        <div className='flex gap-1.5 items-center'>
          <input
            value={draftActor}
            onChange={(e) => setDraftActor(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Escape') cancelEdit() }}
            placeholder='アクター'
            className='w-27.5 shrink-0 text-[11px] border border-(--bp-border) rounded px-1.5 py-1 bg-(--bp-surface) text-(--bp-ink2) outline-none'
          />
          <input
            autoFocus
            value={draftEvent}
            onChange={(e) => setDraftEvent(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); commitEdit() }
              if (e.key === 'Escape') cancelEdit()
            }}
            placeholder='イベント名'
            className='flex-1 text-xs font-semibold border border-(--bp-accent) rounded px-1.5 py-1 bg-(--bp-surface) text-(--bp-ink) outline-none'
          />
          <select
            value={draftStepType}
            onChange={(e) => setDraftStepType(e.target.value as StepType)}
            className='shrink-0 text-[11px] border border-(--bp-border) rounded px-1.5 py-1 bg-(--bp-surface) text-(--bp-ink2)'
          >
            <option value='normal'>{stepTypeLabel('normal')}</option>
            <option value='exception'>{stepTypeLabel('exception')}</option>
            <option value='loop'>{stepTypeLabel('loop')}</option>
            <option value='parallel'>{stepTypeLabel('parallel')}</option>
          </select>
        </div>
        <textarea
          value={draftDescription}
          onChange={(e) => setDraftDescription(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Escape') cancelEdit() }}
          placeholder='説明（省略可）'
          rows={2}
          className='w-full resize-y text-[11px] border border-(--bp-border) rounded px-1.5 py-0.75 bg-(--bp-surface) text-(--bp-ink2) outline-none'
        />
        <div className='flex gap-1 justify-end'>
          <button
            onClick={cancelEdit}
            className='text-[11px] px-2 py-0.5 border border-(--bp-border) rounded bg-(--bp-surface2) text-(--bp-ink2) cursor-pointer'
          >
            キャンセル
          </button>
          <button
            onClick={commitEdit}
            className='text-[11px] px-2 py-0.5 border-none rounded bg-(--bp-accent) text-white cursor-pointer'
          >
            保存
          </button>
        </div>
      </Card>
    )
  }

  return (
    <Card className='flex items-center gap-3 p-2.5 rounded-lg shadow-none'>
      <div
        className={cn(
          'size-5.5 rounded-full flex items-center justify-center text-[10px] font-bold text-white shrink-0',
          isException ? 'bg-(--bp-warning)' : 'bg-(--bp-accent)'
        )}
      >
        {isException ? `E${index}` : index}
      </div>
      <div
        className={cn(
          'text-[11px] font-semibold px-2.25 py-0.75 rounded-full whitespace-nowrap shrink-0 min-w-20 text-center box-border',
          isException ? 'bg-(--bp-warning-pale) text-(--bp-warning)' : 'bg-(--bp-accent-pale) text-(--bp-accent)'
        )}
      >
        {step.actor}
      </div>
      <div className='text-[13px] font-semibold text-(--bp-ink) min-w-30'>{step.event}</div>
      {(step.description != null && step.description !== '') && (
        <>
          <div className='text-[13px] text-(--bp-border-s) shrink-0'>→</div>
          <div className='text-[11px] text-(--bp-ink3)'>{step.description}</div>
        </>
      )}
      <div className='flex-1' />
      {step.source === 'implicit' && (
        <InferenceTypeBadge
          tag={{
            icon: '💭',
            label: '推論',
            style: {
              background: 'var(--bp-accent-pale)',
              color: 'var(--bp-ai)',
              flexShrink: 0
            }
          }}
          reasoning={entities.find((e) => e.entity_role === 'event' && e.name === step.event)?.reasoning}
        />
      )}
      <span
        className={cn(
          'text-[10px] font-bold px-1.75 py-0.5 rounded border whitespace-nowrap',
          isException
            ? 'bg-(--bp-warning-pale) text-(--bp-warning) border-(--bp-warning-border)'
            : 'bg-(--bp-surface2) text-(--bp-ink3) border-(--bp-border)'
        )}
      >
        {stepTypeLabel(step.step_type)}
      </span>
      <button
        title='ステップを修正'
        onClick={startEdit}
        className='ml-1 size-6 flex items-center justify-center bg-(--bp-surface2) border border-(--bp-border) rounded-[5px] cursor-pointer text-(--bp-ink3) p-0 shrink-0'
      >
        <PencilIcon width={12} height={12} />
      </button>
    </Card>
  )
}
